import { useState } from 'react';
import { useTheme, type ThemeConfig } from '@/context/ThemeContext';

const SIDEBAR_PRESETS = [
  { label: 'Noite', bg: '#0f172a', accent: '#6366f1', style: 'dark' as const },
  { label: 'Carvão', bg: '#18181b', accent: '#f59e0b', style: 'dark' as const },
  { label: 'Floresta', bg: '#14532d', accent: '#4ade80', style: 'dark' as const },
  { label: 'Oceano', bg: '#0c4a6e', accent: '#38bdf8', style: 'dark' as const },
  { label: 'Vinho', bg: '#4c0519', accent: '#fb7185', style: 'dark' as const },
  { label: 'Branco', bg: '#ffffff', accent: '#0f172a', style: 'light' as const },
  { label: 'Cinza', bg: '#f4f4f5', accent: '#18181b', style: 'light' as const },
  { label: 'Ardósia', bg: '#1e293b', accent: '#94a3b8', style: 'dark' as const },
];

const PAGE_BG_PRESETS = [
  { label: 'Creme', value: '#f8f7f4' },
  { label: 'Branco', value: '#ffffff' },
  { label: 'Gelo', value: '#f0f4f8' },
  { label: 'Areia', value: '#faf7f2' },
  { label: 'Névoa', value: '#f5f5f5' },
  { label: 'Menta', value: '#f0faf4' },
];

const PRIMARY_PRESETS = [
  { label: 'Ardósia', value: '#0f172a' },
  { label: 'Carvão', value: '#18181b' },
  { label: 'Esmeralda', value: '#065f46' },
  { label: 'Âmbar', value: '#92400e' },
  { label: 'Rosa', value: '#9f1239' },
  { label: 'Índigo', value: '#3730a3' },
];

const FONT_OPTIONS = [
  { label: 'Inter', value: 'Inter', preview: 'Aa' },
  { label: 'DM Sans', value: 'DM Sans', preview: 'Aa' },
  { label: 'Nunito', value: 'Nunito', preview: 'Aa' },
  { label: 'Outfit', value: 'Outfit', preview: 'Aa' },
  { label: 'Plus Jakarta Sans', value: 'Plus Jakarta Sans', preview: 'Aa' },
];

const FONT_SIZE_OPTIONS: { label: string; value: ThemeConfig['fontSize'] }[] = [
  { label: 'Pequena', value: 'sm' },
  { label: 'Normal', value: 'md' },
  { label: 'Grande', value: 'lg' },
];

const FONT_WEIGHT_OPTIONS: { label: string; value: ThemeConfig['fontWeight'] }[] = [
  { label: 'Leve', value: 'light' },
  { label: 'Normal', value: 'normal' },
  { label: 'Médio', value: 'medium' },
];

const RADIUS_OPTIONS: { label: string; value: ThemeConfig['borderRadius']; preview: string }[] = [
  { label: 'Reto', value: 'sharp', preview: 'rounded-sm' },
  { label: 'Suave', value: 'rounded', preview: 'rounded-lg' },
  { label: 'Pill', value: 'pill', preview: 'rounded-full' },
];

// ── Galeria de ícones sugeridos por página ──
const ICON_GALLERY: Record<string, { icon: string; label: string }[]> = {
  '/progressoes': [
    { icon: 'ri-bar-chart-line', label: 'Barras' },
    { icon: 'ri-bar-chart-2-line', label: 'Barras 2' },
    { icon: 'ri-line-chart-line', label: 'Linha' },
    { icon: 'ri-arrow-up-circle-line', label: 'Seta cima' },
    { icon: 'ri-rocket-line', label: 'Foguete' },
    { icon: 'ri-arrow-up-line', label: 'Tendência' },
    { icon: 'ri-stock-line', label: 'Fundos' },
    { icon: 'ri-exchange-line', label: 'Variação' },
    { icon: 'ri-heart-pulse-line', label: 'Pulso' },
    { icon: 'ri-dashboard-2-line', label: 'Dashboard' },
    { icon: 'ri-speed-up-line', label: 'Velocidade' },
    { icon: 'ri-pie-chart-line', label: 'Pizza' },
  ],
  '/funcionarios': [
    { icon: 'ri-team-line', label: 'Equipa' },
    { icon: 'ri-user-line', label: 'Utilizador' },
    { icon: 'ri-user-3-line', label: 'Pessoa' },
    { icon: 'ri-group-line', label: 'Grupo' },
    { icon: 'ri-contacts-line', label: 'Contactos' },
    { icon: 'ri-account-circle-line', label: 'Conta' },
    { icon: 'ri-user-star-line', label: 'Estrela' },
    { icon: 'ri-user-settings-line', label: 'Config' },
    { icon: 'ri-parent-line', label: 'Pessoa 2' },
    { icon: 'ri-id-card-line', label: 'Cartão' },
    { icon: 'ri-profile-line', label: 'Perfil' },
    { icon: 'ri-community-line', label: 'Comunidade' },
  ],
  '/siadap': [
    { icon: 'ri-award-line', label: 'Prémio' },
    { icon: 'ri-medal-line', label: 'Medalha' },
    { icon: 'ri-star-line', label: 'Estrela' },
    { icon: 'ri-trophy-line', label: 'Troféu' },
    { icon: 'ri-shield-check-line', label: 'Escudo' },
    { icon: 'ri-verified-badge-line', label: 'Badge' },
    { icon: 'ri-checkbox-circle-line', label: 'Check' },
    { icon: 'ri-file-list-3-line', label: 'Lista' },
    { icon: 'ri-survey-line', label: 'Inquérito' },
    { icon: 'ri-clipboard-line', label: 'Clipboard' },
    { icon: 'ri-task-line', label: 'Tarefa' },
    { icon: 'ri-government-line', label: 'Governo' },
  ],
  '/historico': [
    { icon: 'ri-history-line', label: 'Histórico' },
    { icon: 'ri-time-line', label: 'Tempo' },
    { icon: 'ri-calendar-line', label: 'Calendário' },
    { icon: 'ri-archive-line', label: 'Arquivo' },
    { icon: 'ri-file-history-line', label: 'Ficheiro' },
    { icon: 'ri-database-line', label: 'Base dados' },
    { icon: 'ri-folder-history-line', label: 'Pasta' },
    { icon: 'ri-loop-right-line', label: 'Loop' },
    { icon: 'ri-refresh-line', label: 'Refresh' },
    { icon: 'ri-time-fill', label: 'Relógio' },
    { icon: 'ri-calendar-check-line', label: 'Cal. check' },
    { icon: 'ri-book-open-line', label: 'Livro' },
  ],
  '/resumo': [
    { icon: 'ri-pie-chart-2-line', label: 'Pizza 2' },
    { icon: 'ri-donut-chart-fill', label: 'Donut' },
    { icon: 'ri-bar-chart-box-line', label: 'Agrupado' },
    { icon: 'ri-bubble-chart-line', label: 'Bolhas' },
    { icon: 'ri-layout-grid-line', label: 'Grid' },
    { icon: 'ri-table-line', label: 'Tabela' },
    { icon: 'ri-file-chart-line', label: 'Relatório' },
    { icon: 'ri-eye-line', label: 'Vista' },
    { icon: 'ri-focus-3-line', label: 'Foco' },
    { icon: 'ri-list-check-3', label: 'Lista check' },
    { icon: 'ri-node-tree', label: 'Org.' },
    { icon: 'ri-share-line', label: 'Mapa' },
  ],
  '/relatorios': [
    { icon: 'ri-file-chart-2-line', label: 'Gráfico' },
    { icon: 'ri-file-list-3-line', label: 'Lista' },
    { icon: 'ri-file-text-line', label: 'Texto' },
    { icon: 'ri-file-pdf-line', label: 'PDF' },
    { icon: 'ri-printer-line', label: 'Imprimir' },
    { icon: 'ri-download-2-line', label: 'Download' },
    { icon: 'ri-bar-chart-grouped-line', label: 'Barras' },
    { icon: 'ri-article-line', label: 'Artigo' },
    { icon: 'ri-newspaper-line', label: 'Jornal' },
    { icon: 'ri-clipboard-line', label: 'Clipboard' },
    { icon: 'ri-survey-line', label: 'Inquérito' },
    { icon: 'ri-book-3-line', label: 'Livro' },
  ],
};

export default function AppearanceTab() {
  const { theme, setTheme, saveTheme, saving } = useTheme();
  const [saved, setSaved] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string>(theme.logoUrl || '');
  const [heroPreview, setHeroPreview] = useState<string>(theme.heroImageUrl || '');
  const [faviconPreview, setFaviconPreview] = useState<string>(theme.faviconUrl || '');
  const [pageBgPreview, setPageBgPreview] = useState<string>(theme.pageBgImage || '');
  const [openGallery, setOpenGallery] = useState<string | null>(null);

  const handleSave = async () => {
    await saveTheme();
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'logoUrl' | 'heroImageUrl' | 'pageBgImage',
    setPreview: (v: string) => void
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setPreview(result);
      setTheme({ [field]: result });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6">

      {/* ── Identidade ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-building-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Identidade</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Nome e subtítulo que aparecem no topo da barra lateral</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                Nome da Câmara / Organização
              </label>
              <input
                type="text"
                value={theme.nomeCamara ?? 'Câmara Municipal'}
                onChange={(e) => setTheme({ nomeCamara: e.target.value })}
                placeholder="Câmara Municipal de..."
                className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 transition-colors"
              />
              <p className="text-[11px] text-zinc-400 mt-1.5">Aparece em destaque no topo da sidebar</p>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                Subtítulo da Sidebar
              </label>
              <input
                type="text"
                value={theme.subtituloSidebar ?? 'SIADAP · Ciclo 2025'}
                onChange={(e) => setTheme({ subtituloSidebar: e.target.value })}
                placeholder="SIADAP · Ciclo 2025"
                className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 transition-colors"
              />
              <p className="text-[11px] text-zinc-400 mt-1.5">Linha secundária abaixo do nome</p>
            </div>
          </div>

          <div className="border-t border-zinc-100 pt-5">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
                  Título da Página (separador do browser)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={theme.pageTitle ?? ''}
                    onChange={(e) => setTheme({ pageTitle: e.target.value })}
                    placeholder="Ex: SIADAP · Câmara Municipal de Lisboa"
                    className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 transition-colors pr-24"
                  />
                  {theme.pageTitle && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-zinc-400">
                      {theme.pageTitle.length} car.
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-zinc-400 mt-1.5">
                  Texto que aparece no separador do browser. Se vazio, usa o título padrão da aplicação.
                </p>
              </div>
              {/* Preview do tab */}
              <div className="flex-shrink-0 mt-6">
                <div className="bg-zinc-100 rounded-t-lg px-3 py-1.5 flex items-center gap-1.5 w-fit max-w-[200px]">
                  {theme.faviconUrl ? (
                    <img src={theme.faviconUrl} alt="favicon" className="w-3.5 h-3.5 object-contain flex-shrink-0" />
                  ) : (
                    <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-global-line text-zinc-400 text-xs" />
                    </div>
                  )}
                  <span className="text-[11px] text-zinc-600 truncate font-medium">
                    {theme.pageTitle || theme.nomeCamara || 'SIADAP'}
                  </span>
                </div>
                <div className="bg-white border border-zinc-200 rounded-b-lg rounded-tr-lg h-5 w-full max-w-[200px]" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Navegação ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-menu-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Itens de Navegação</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Escolha quais os itens do menu lateral que aparecem em negrito</p>
          </div>
        </div>
        <div className="p-6">
          <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Negrito nos itens do menu</p>
          <div className="space-y-2">
            {[
              { to: '/funcionarios', label: 'Funcionários', icon: 'ri-team-line'         },
              { to: '/siadap',       label: 'SIADAP',       icon: 'ri-award-line'        },
              { to: '/historico',    label: 'Histórico',    icon: 'ri-history-line'      },
              { to: '/resumo',       label: 'Resumo',       icon: 'ri-pie-chart-2-line'  },
              { to: '/progressoes',  label: 'Progressões',  icon: 'ri-bar-chart-line'    },
              { to: '/relatorios',   label: 'Relatórios',   icon: 'ri-file-chart-2-line' },
            ].map((item) => {
              const boldItems = theme.navBoldItems ?? ['/progressoes'];
              const isBold = boldItems.includes(item.to);
              return (
                <label
                  key={item.to}
                  className="flex items-center justify-between px-4 py-3 rounded-xl border border-zinc-100 hover:border-zinc-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-zinc-500 text-sm`} />
                    </div>
                    <span
                      className="text-[13px] text-zinc-700"
                      style={{ fontWeight: isBold ? 700 : 400 }}
                    >
                      {item.label}
                    </span>
                    {isBold && (
                      <span className="text-[10px] font-semibold text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-full">Bold</span>
                    )}
                  </div>
                  <div
                    onClick={() => {
                      const current = theme.navBoldItems ?? ['/'];
                      const next = isBold
                        ? current.filter((x) => x !== item.to)
                        : [...current, item.to];
                      setTheme({ navBoldItems: next });
                    }}
                    className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${isBold ? 'bg-zinc-900' : 'bg-zinc-200'}`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isBold ? 'translate-x-5' : 'translate-x-0.5'}`}
                    />
                  </div>
                </label>
              );
            })}
          </div>
          <p className="text-[11px] text-zinc-400 mt-3">
            Os itens em negrito ficam com <strong>font-weight: 700</strong> na barra lateral. Útil para destacar páginas principais.
          </p>
        </div>
      </div>

      {/* ── Ícones da Sidebar ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-apps-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Ícones da Sidebar</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Personalize o ícone de cada item do menu lateral — clique em "Galeria" para escolher visualmente</p>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {[
              { to: '/funcionarios', label: 'Funcionários', defaultIcon: 'ri-team-line'         },
              { to: '/siadap',       label: 'SIADAP',       defaultIcon: 'ri-award-line'        },
              { to: '/historico',    label: 'Histórico',    defaultIcon: 'ri-history-line'      },
              { to: '/resumo',       label: 'Resumo',       defaultIcon: 'ri-pie-chart-2-line'  },
              { to: '/progressoes',  label: 'Progressões',  defaultIcon: 'ri-bar-chart-line'    },
              { to: '/relatorios',   label: 'Relatórios',   defaultIcon: 'ri-file-chart-2-line' },
            ].map((item) => {
              const currentIcon = (theme.navIcons ?? {})[item.to] || item.defaultIcon;
              const isGalleryOpen = openGallery === item.to;
              const suggestions = ICON_GALLERY[item.to] ?? [];
              return (
                <div key={item.to} className="rounded-xl border border-zinc-100 overflow-hidden">
                  {/* Row principal */}
                  <div className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50/50 transition-colors">
                    {/* Preview do ícone */}
                    <div
                      className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0"
                      style={{ backgroundColor: theme.sidebarBg }}
                    >
                      <i className={`${currentIcon} text-base`} style={{ color: theme.sidebarAccent }} />
                    </div>
                    <span className="text-[13px] font-medium text-zinc-700 w-28 flex-shrink-0">{item.label}</span>
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={currentIcon}
                        onChange={(e) => {
                          const next = { ...(theme.navIcons ?? {}), [item.to]: e.target.value };
                          setTheme({ navIcons: next });
                        }}
                        placeholder={item.defaultIcon}
                        className="w-full px-3 py-2 text-[12px] font-mono border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 transition-colors"
                      />
                    </div>
                    {/* Botão galeria */}
                    <button
                      onClick={() => setOpenGallery(isGalleryOpen ? null : item.to)}
                      className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold rounded-lg border transition-colors cursor-pointer flex-shrink-0 ${
                        isGalleryOpen
                          ? 'bg-zinc-900 text-white border-zinc-900'
                          : 'text-zinc-500 border-zinc-200 hover:border-zinc-400 hover:text-zinc-700'
                      }`}
                    >
                      <i className="ri-grid-line text-xs" />
                      Galeria
                    </button>
                    {/* Reset */}
                    <button
                      onClick={() => {
                        const next = { ...(theme.navIcons ?? {}) };
                        delete next[item.to];
                        setTheme({ navIcons: next });
                      }}
                      className="whitespace-nowrap w-8 h-8 flex items-center justify-center text-zinc-300 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                      title="Repor ícone padrão"
                    >
                      <i className="ri-refresh-line text-sm" />
                    </button>
                  </div>

                  {/* Galeria de sugestões */}
                  {isGalleryOpen && (
                    <div className="border-t border-zinc-100 px-4 py-4 bg-zinc-50/60">
                      <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">
                        Ícones sugeridos para "{item.label}"
                      </p>
                      <div className="grid grid-cols-6 gap-2">
                        {suggestions.map((s) => {
                          const isSelected = currentIcon === s.icon;
                          return (
                            <button
                              key={s.icon}
                              onClick={() => {
                                const next = { ...(theme.navIcons ?? {}), [item.to]: s.icon };
                                setTheme({ navIcons: next });
                                setOpenGallery(null);
                              }}
                              title={s.label}
                              className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                                isSelected
                                  ? 'border-zinc-900 bg-zinc-900'
                                  : 'border-zinc-200 bg-white hover:border-zinc-400 hover:bg-zinc-50'
                              }`}
                            >
                              {/* Preview com cores da sidebar */}
                              <div
                                className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                                style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.15)' : theme.sidebarBg }}
                              >
                                <i
                                  className={`${s.icon} text-sm`}
                                  style={{ color: isSelected ? '#ffffff' : theme.sidebarAccent }}
                                />
                              </div>
                              <span className={`text-[10px] font-medium leading-tight text-center ${isSelected ? 'text-white' : 'text-zinc-500'}`}>
                                {s.label}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-zinc-400 mt-3">
                        Clique num ícone para selecionar. Pode também escrever qualquer classe Remix Icon no campo de texto acima.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 px-3 py-3 bg-zinc-50 rounded-xl">
            <p className="text-[11px] text-zinc-500 font-medium mb-1">Como usar</p>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Clique em <strong>Galeria</strong> para escolher visualmente, ou escreva diretamente a classe Remix Icon, ex: <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-zinc-200">ri-home-line</span>.
              Explore todos os ícones em <span className="font-mono text-zinc-600">remixicon.com</span>.
            </p>
          </div>
        </div>
      </div>

      {/* ── Sidebar ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-layout-left-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Barra Lateral</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Cor de fundo e destaque da sidebar</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Presets</p>
            <div className="grid grid-cols-4 gap-2">
              {SIDEBAR_PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => setTheme({ sidebarBg: p.bg, sidebarAccent: p.accent, sidebarStyle: p.style })}
                  className={`relative flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                    theme.sidebarBg === p.bg
                      ? 'border-zinc-900 scale-[1.02]'
                      : 'border-zinc-100 hover:border-zinc-300'
                  }`}
                >
                  <div
                    className="w-full h-10 rounded-lg flex items-center justify-center gap-1.5"
                    style={{ backgroundColor: p.bg }}
                  >
                    <div className="w-1.5 h-5 rounded-full opacity-40" style={{ backgroundColor: p.accent }} />
                    <div className="w-1.5 h-3 rounded-full opacity-25" style={{ backgroundColor: p.accent }} />
                    <div className="w-1.5 h-4 rounded-full opacity-25" style={{ backgroundColor: p.accent }} />
                  </div>
                  <span className="text-[11px] font-medium text-zinc-600">{p.label}</span>
                  {theme.sidebarBg === p.bg && (
                    <div className="absolute top-1.5 right-1.5 w-4 h-4 flex items-center justify-center bg-zinc-900 rounded-full">
                      <i className="ri-check-line text-white text-[9px]" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Cor de Fundo</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={theme.sidebarBg}
                  onChange={(e) => setTheme({ sidebarBg: e.target.value })}
                  className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={theme.sidebarBg}
                  onChange={(e) => setTheme({ sidebarBg: e.target.value })}
                  className="flex-1 px-3 py-2 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Cor de Destaque</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={theme.sidebarAccent}
                  onChange={(e) => setTheme({ sidebarAccent: e.target.value })}
                  className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5"
                />
                <input
                  type="text"
                  value={theme.sidebarAccent}
                  onChange={(e) => setTheme({ sidebarAccent: e.target.value })}
                  className="flex-1 px-3 py-2 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 font-mono"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Imagem de Fundo da Página Central ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-image-2-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Imagem de Fundo da Página</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Imagem decorativa na área de conteúdo central — aparece subtilmente por baixo do conteúdo</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          {/* Upload + preview */}
          <div className="flex gap-6 items-start">
            {/* Zona de upload */}
            <div className="flex-shrink-0 w-52">
              <div className={`border-2 border-dashed rounded-xl overflow-hidden transition-colors ${pageBgPreview ? 'border-zinc-300' : 'border-zinc-200 hover:border-zinc-400'}`}>
                {pageBgPreview ? (
                  <div className="relative">
                    <img
                      src={pageBgPreview}
                      alt="Fundo da página"
                      className="w-full h-32 object-cover"
                    />
                    {/* Overlay de opacidade para preview real */}
                    <div
                      className="absolute inset-0 bg-white"
                      style={{ opacity: 1 - (theme.pageBgImageOpacity ?? 15) / 100 }}
                    />
                    <button
                      onClick={() => {
                        setPageBgPreview('');
                        setTheme({ pageBgImage: '' });
                      }}
                      className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer hover:bg-red-600 transition-colors z-10"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                    <div className="absolute bottom-2 left-2 z-10">
                      <span className="text-[10px] font-semibold bg-black/50 text-white px-2 py-0.5 rounded-full">
                        Preview com {theme.pageBgImageOpacity ?? 15}% opacidade
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 p-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-zinc-100 rounded-xl">
                      <i className="ri-image-add-line text-2xl text-zinc-400" />
                    </div>
                    <p className="text-[12px] text-zinc-400 text-center leading-relaxed">
                      Sem imagem de fundo
                    </p>
                  </div>
                )}
              </div>
              <label className="mt-3 flex items-center justify-center gap-2 w-full px-4 py-2.5 border border-zinc-200 rounded-xl text-[12px] font-medium text-zinc-600 hover:text-zinc-900 hover:border-zinc-400 transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-upload-2-line text-sm" />
                </div>
                {pageBgPreview ? 'Alterar imagem' : 'Carregar imagem'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e, 'pageBgImage', setPageBgPreview)}
                />
              </label>
              <p className="text-[11px] text-zinc-400 mt-2 text-center">PNG, JPG ou WebP · Qualquer dimensão</p>
            </div>

            {/* Controlos */}
            <div className="flex-1 space-y-5">
              {/* Opacidade */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest">
                    Opacidade da Imagem
                  </label>
                  {/* Controlo numérico: - input + */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setTheme({ pageBgImageOpacity: Math.max(3, (theme.pageBgImageOpacity ?? 15) - 1) })}
                      disabled={!pageBgPreview || (theme.pageBgImageOpacity ?? 15) <= 3}
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 hover:border-zinc-300 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-subtract-line text-xs" />
                    </button>
                    <div className="relative">
                      <input
                        type="number"
                        min={3}
                        max={100}
                        value={theme.pageBgImageOpacity ?? 15}
                        onChange={(e) => {
                          const v = Math.min(100, Math.max(3, parseInt(e.target.value, 10) || 3));
                          setTheme({ pageBgImageOpacity: v });
                        }}
                        disabled={!pageBgPreview}
                        className="w-16 text-center px-1 py-1 text-[13px] font-bold text-zinc-800 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 disabled:opacity-40 disabled:cursor-not-allowed [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-zinc-400 pointer-events-none">%</span>
                    </div>
                    <button
                      onClick={() => setTheme({ pageBgImageOpacity: Math.min(100, (theme.pageBgImageOpacity ?? 15) + 1) })}
                      disabled={!pageBgPreview || (theme.pageBgImageOpacity ?? 15) >= 100}
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 hover:border-zinc-300 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <i className="ri-add-line text-xs" />
                    </button>
                  </div>
                </div>
                <input
                  type="range"
                  min={3}
                  max={100}
                  step={1}
                  value={theme.pageBgImageOpacity ?? 15}
                  onChange={(e) => setTheme({ pageBgImageOpacity: parseInt(e.target.value, 10) })}
                  disabled={!pageBgPreview}
                  className="w-full accent-zinc-900 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                />
                <div className="flex justify-between text-[10px] text-zinc-400 mt-0.5">
                  <span>Muito subtil (3%)</span>
                  <span>Total (100%)</span>
                </div>
                <p className="text-[11px] text-zinc-400 mt-1.5">
                  A imagem sobrepõe-se ao conteúdo da página. Recomendado entre 5–20% para não dificultar a leitura.
                </p>
              </div>

              {/* Posição da imagem */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">
                  Posição da Imagem
                </label>
                <div className="flex gap-2">
                  {([
                    { value: 'top',    label: 'Topo',   icon: 'ri-align-top'    },
                    { value: 'center', label: 'Centro', icon: 'ri-align-vertically' },
                    { value: 'bottom', label: 'Baixo',  icon: 'ri-align-bottom' },
                  ] as { value: 'top' | 'center' | 'bottom'; label: string; icon: string }[]).map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setTheme({ pageBgImagePosition: opt.value })}
                      disabled={!pageBgPreview}
                      className={`flex-1 flex flex-col items-center gap-1.5 px-3 py-2.5 rounded-xl border-2 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                        (theme.pageBgImagePosition ?? 'center') === opt.value
                          ? 'border-zinc-900 bg-zinc-900 text-white'
                          : 'border-zinc-100 hover:border-zinc-300 text-zinc-600'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${opt.icon} text-sm`} />
                      </div>
                      <span className="text-[11px] font-semibold">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Efeito Parallax */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">
                  Efeito Parallax
                </label>
                <label className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-colors cursor-pointer ${
                  theme.pageBgImageFixed ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-100 hover:border-zinc-200'
                } ${!pageBgPreview ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
                      <i className="ri-landscape-line text-zinc-600 text-sm" />
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-zinc-700">Imagem fixa (parallax)</p>
                      <p className="text-[11px] text-zinc-400">A imagem fica estática enquanto o conteúdo faz scroll</p>
                    </div>
                  </div>
                  <div
                    onClick={() => { if (pageBgPreview) setTheme({ pageBgImageFixed: !theme.pageBgImageFixed }); }}
                    className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${theme.pageBgImageFixed ? 'bg-zinc-900' : 'bg-zinc-200'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${theme.pageBgImageFixed ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </div>
                </label>
              </div>

              {/* Modo de ajuste */}
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">
                  Modo de Ajuste
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { value: 'cover',   label: 'Preencher',  icon: 'ri-fullscreen-line',       desc: 'Cobre toda a área' },
                    { value: 'contain', label: 'Conter',     icon: 'ri-fullscreen-exit-line',  desc: 'Imagem completa visível' },
                    { value: 'repeat',  label: 'Repetir',    icon: 'ri-grid-line',             desc: 'Padrão em mosaico' },
                    { value: 'center',  label: 'Centrar',    icon: 'ri-focus-3-line',          desc: 'Tamanho original, centrado' },
                  ] as { value: 'cover' | 'contain' | 'repeat' | 'center'; label: string; icon: string; desc: string }[]).map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setTheme({ pageBgImageFit: opt.value })}
                      disabled={!pageBgPreview}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-left transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                        (theme.pageBgImageFit ?? 'cover') === opt.value
                          ? 'border-zinc-900 bg-zinc-50'
                          : 'border-zinc-100 hover:border-zinc-300'
                      }`}
                    >
                      <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
                        <i className={`${opt.icon} text-zinc-600 text-sm`} />
                      </div>
                      <div>
                        <p className="text-[12px] font-semibold text-zinc-700">{opt.label}</p>
                        <p className="text-[10px] text-zinc-400">{opt.desc}</p>
                      </div>
                      {(theme.pageBgImageFit ?? 'cover') === opt.value && (
                        <div className="ml-auto w-4 h-4 flex items-center justify-center bg-zinc-900 rounded-full flex-shrink-0">
                          <i className="ri-check-line text-white text-[9px]" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Dica */}
              {!pageBgPreview && (
                <div className="flex items-start gap-2.5 p-3.5 bg-zinc-50 border border-zinc-100 rounded-xl">
                  <div className="w-5 h-5 flex items-center justify-center text-zinc-400 flex-shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-sm" />
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold text-zinc-600 mb-0.5">Sugestões de imagens</p>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                      Texturas subtis, padrões geométricos ou fotografias desfocadas funcionam melhor. Evite imagens com muito contraste para não dificultar a leitura.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Preview em contexto */}
          {pageBgPreview && (
            <div className="border-t border-zinc-100 pt-5">
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Pré-visualização em contexto</p>
              <div
                className="relative rounded-xl overflow-hidden border border-zinc-200 h-36"
                style={{
                  backgroundColor: theme.pageBg,
                }}
              >
                {/* Camada da imagem */}
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `url(${pageBgPreview})`,
                    backgroundSize: (theme.pageBgImageFit ?? 'cover') === 'repeat' ? 'auto' : (theme.pageBgImageFit ?? 'cover') === 'contain' ? 'contain' : 'cover',
                    backgroundRepeat: (theme.pageBgImageFit ?? 'cover') === 'repeat' ? 'repeat' : 'no-repeat',
                    backgroundPosition: 'center',
                    opacity: (theme.pageBgImageOpacity ?? 15) / 100,
                  }}
                />
                {/* Conteúdo simulado */}
                <div className="relative z-10 p-4 flex flex-col gap-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-white border border-zinc-100" />
                    <div className="space-y-1">
                      <div className="h-2 w-24 bg-zinc-200 rounded" />
                      <div className="h-3 w-36 bg-zinc-800 rounded opacity-80" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex-1 h-12 bg-white rounded-lg border border-zinc-100 p-2 flex flex-col gap-1">
                        <div className="h-1.5 w-8 bg-zinc-200 rounded" />
                        <div className="h-2.5 w-6 bg-zinc-700 rounded opacity-70" />
                      </div>
                    ))}
                  </div>
                  <div className="h-6 bg-white rounded-lg border border-zinc-100" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-400 mt-2">
                Esta é uma simulação — o resultado real pode variar ligeiramente.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Fundo da Página ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-palette-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Cores da Página</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Fundo geral e cor primária dos botões</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Fundo da Página</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {PAGE_BG_PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setTheme({ pageBg: p.value })}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-[12px] font-medium transition-all cursor-pointer ${
                    theme.pageBg === p.value
                      ? 'border-zinc-900 text-zinc-900'
                      : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                  }`}
                >
                  <div
                    className="w-4 h-4 rounded-full border border-zinc-200"
                    style={{ backgroundColor: p.value }}
                  />
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={theme.pageBg}
                onChange={(e) => setTheme({ pageBg: e.target.value })}
                className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5"
              />
              <input
                type="text"
                value={theme.pageBg}
                onChange={(e) => setTheme({ pageBg: e.target.value })}
                className="w-36 px-3 py-2 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 font-mono"
              />
            </div>
          </div>

          <div>
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Cor Primária (Botões)</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {PRIMARY_PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setTheme({ primaryColor: p.value })}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-[12px] font-medium transition-all cursor-pointer ${
                    theme.primaryColor === p.value
                      ? 'border-zinc-900 text-zinc-900'
                      : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                  }`}
                >
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: p.value }}
                  />
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={theme.primaryColor}
                onChange={(e) => setTheme({ primaryColor: e.target.value })}
                className="w-10 h-10 rounded-lg border border-zinc-200 cursor-pointer p-0.5"
              />
              <input
                type="text"
                value={theme.primaryColor}
                onChange={(e) => setTheme({ primaryColor: e.target.value })}
                className="w-36 px-3 py-2 text-[13px] border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-300 font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Tipografia ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-text text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Tipografia</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Tipo de letra, tamanho e peso</p>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Tipo de Letra</p>
            <div className="grid grid-cols-5 gap-2">
              {FONT_OPTIONS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setTheme({ fontFamily: f.value })}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                    theme.fontFamily === f.value
                      ? 'border-zinc-900 bg-zinc-900 text-white'
                      : 'border-zinc-100 hover:border-zinc-300'
                  }`}
                >
                  <span
                    className="text-2xl font-semibold text-zinc-800"
                    style={{ fontFamily: `'${f.value}', sans-serif` }}
                  >
                    {f.preview}
                  </span>
                  <span className="text-[11px] font-medium text-zinc-500 text-center leading-tight">{f.label}</span>
                  {theme.fontFamily === f.value && (
                    <div className="w-4 h-4 flex items-center justify-center bg-zinc-900 rounded-full">
                      <i className="ri-check-line text-white text-[9px]" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Tamanho</p>
              <div className="flex gap-2">
                {FONT_SIZE_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setTheme({ fontSize: o.value })}
                    className={`flex-1 py-2 rounded-lg border-2 text-[12px] font-medium transition-all cursor-pointer ${
                      theme.fontSize === o.value
                        ? 'border-zinc-900 bg-zinc-900 text-white'
                        : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Peso</p>
              <div className="flex gap-2">
                {FONT_WEIGHT_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    onClick={() => setTheme({ fontWeight: o.value })}
                    className={`flex-1 py-2 rounded-lg border-2 text-[12px] font-medium transition-all cursor-pointer ${
                      theme.fontWeight === o.value
                        ? 'border-zinc-900 bg-zinc-900 text-white'
                        : 'border-zinc-100 text-zinc-500 hover:border-zinc-300'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Cantos dos Elementos</p>
            <div className="flex gap-3">
              {RADIUS_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  onClick={() => setTheme({ borderRadius: o.value })}
                  className={`flex-1 flex flex-col items-center gap-2 p-4 border-2 transition-all cursor-pointer ${
                    theme.borderRadius === o.value
                      ? 'border-zinc-900 bg-zinc-50'
                      : 'border-zinc-100 hover:border-zinc-300'
                  } ${o.preview}`}
                >
                  <div
                    className={`w-10 h-6 bg-zinc-800 ${o.preview}`}
                  />
                  <span className="text-[12px] font-medium text-zinc-600">{o.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Imagens ── */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-image-line text-zinc-600 text-sm" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-zinc-800">Imagens</h2>
            <p className="text-[11px] text-zinc-400 mt-0.5">Logótipo, favicon e imagem de fundo da sidebar</p>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Logo */}
            <div>
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Logótipo</p>
              <div className="border-2 border-dashed border-zinc-200 rounded-xl p-4 flex flex-col items-center gap-3 hover:border-zinc-400 transition-colors">
                {logoPreview ? (
                  <div className="relative w-full">
                    <img src={logoPreview} alt="Logo" className="w-full object-contain rounded-lg" style={{ height: `${theme.logoSize ?? 26}px`, maxHeight: '80px' }} />
                    <button
                      onClick={() => { setLogoPreview(''); setTheme({ logoUrl: '' }); }}
                      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer hover:bg-red-600 transition-colors"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center bg-zinc-100 rounded-xl">
                    <i className="ri-image-add-line text-2xl text-zinc-400" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <span className="text-[12px] font-medium text-zinc-600 hover:text-zinc-900 transition-colors">
                    {logoPreview ? 'Alterar imagem' : 'Carregar logótipo'}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'logoUrl', setLogoPreview)} />
                </label>
                <p className="text-[11px] text-zinc-400 text-center">PNG, SVG ou JPG · Recomendado 200×60px</p>
              </div>
              {/* Slider tamanho do logo */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">Tamanho do Logótipo</label>
                  <span className="text-[12px] font-bold text-zinc-700">{theme.logoSize ?? 26}px</span>
                </div>
                <input
                  type="range" min={16} max={80} step={2}
                  value={theme.logoSize ?? 26}
                  onChange={(e) => setTheme({ logoSize: parseInt(e.target.value, 10) })}
                  className="w-full accent-zinc-900 cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-zinc-400 mt-0.5">
                  <span>Pequeno</span><span>Grande</span>
                </div>
              </div>
            </div>

            {/* Hero / Fundo */}
            <div>
              <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Imagem de Fundo (Sidebar)</p>
              <div className="border-2 border-dashed border-zinc-200 rounded-xl p-4 flex flex-col items-center gap-3 hover:border-zinc-400 transition-colors">
                {heroPreview ? (
                  <div className="relative w-full">
                    <img src={heroPreview} alt="Hero" className="w-full h-20 object-cover rounded-lg" />
                    <button
                      onClick={() => { setHeroPreview(''); setTheme({ heroImageUrl: '' }); }}
                      className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer hover:bg-red-600 transition-colors"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 flex items-center justify-center bg-zinc-100 rounded-xl">
                    <i className="ri-image-add-line text-2xl text-zinc-400" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <span className="text-[12px] font-medium text-zinc-600 hover:text-zinc-900 transition-colors">
                    {heroPreview ? 'Alterar imagem' : 'Carregar imagem'}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'heroImageUrl', setHeroPreview)} />
                </label>
                <p className="text-[11px] text-zinc-400 text-center">PNG ou JPG · Recomendado 220×800px</p>
              </div>
              {/* Slider visibilidade da imagem de fundo */}
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">Visibilidade da Imagem</label>
                  <span className="text-[12px] font-bold text-zinc-700">{theme.heroImageSize ?? 100}%</span>
                </div>
                <input
                  type="range" min={10} max={100} step={5}
                  value={theme.heroImageSize ?? 100}
                  onChange={(e) => setTheme({ heroImageSize: parseInt(e.target.value, 10) })}
                  className="w-full accent-zinc-900 cursor-pointer"
                  disabled={!heroPreview}
                />
                <div className="flex justify-between text-[10px] text-zinc-400 mt-0.5">
                  <span>Subtil</span><span>Total</span>
                </div>
                {!heroPreview && <p className="text-[10px] text-zinc-400 mt-1">Carregue uma imagem para ativar</p>}
              </div>
            </div>
          </div>

          {/* Cor do texto da navegação */}
          <div className="border-t border-zinc-100 pt-5">
            <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Cor do Texto da Navegação</p>
            <div className="flex gap-2">
              {([
                { value: 'auto', label: 'Automático', desc: 'Adapta ao tema' },
                { value: 'black', label: 'Preto', desc: 'Sempre preto' },
                { value: 'white', label: 'Branco', desc: 'Sempre branco' },
              ] as { value: 'auto' | 'black' | 'white'; label: string; desc: string }[]).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setTheme({ navTextColor: opt.value })}
                  className={`flex-1 flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                    (theme.navTextColor ?? 'auto') === opt.value
                      ? 'border-zinc-900 bg-zinc-50'
                      : 'border-zinc-100 hover:border-zinc-300'
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
                    style={{
                      backgroundColor: theme.sidebarBg,
                      color: opt.value === 'black' ? '#000' : opt.value === 'white' ? '#fff' : (theme.sidebarStyle === 'light' ? '#000' : '#fff'),
                    }}
                  >
                    Aa
                  </div>
                  <span className="text-[12px] font-semibold text-zinc-700">{opt.label}</span>
                  <span className="text-[10px] text-zinc-400">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Favicon */}
          <div className="border-t border-zinc-100 pt-5">
            <div className="flex items-start gap-5">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-[12px] font-semibold text-zinc-500 uppercase tracking-widest">Favicon do Browser</p>
                  <span className="text-[10px] bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-full font-medium">Ícone do separador</span>
                </div>
                <p className="text-[12px] text-zinc-400 mb-4">
                  Ícone que aparece no separador do browser e nos favoritos. Deve ser quadrado e com fundo transparente para melhor resultado.
                </p>
                <div className="flex items-center gap-4">
                  <div className="border-2 border-dashed border-zinc-200 rounded-xl p-4 flex flex-col items-center gap-3 hover:border-zinc-400 transition-colors w-40 flex-shrink-0">
                    {faviconPreview ? (
                      <div className="relative">
                        <img src={faviconPreview} alt="Favicon" className="w-12 h-12 object-contain rounded-lg" />
                        <button
                          onClick={() => { setFaviconPreview(''); setTheme({ faviconUrl: '' }); }}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-red-500 text-white rounded-full cursor-pointer hover:bg-red-600 transition-colors"
                        >
                          <i className="ri-close-line text-[10px]" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-12 h-12 flex items-center justify-center bg-zinc-100 rounded-xl">
                        <i className="ri-global-line text-xl text-zinc-400" />
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <span className="text-[11px] font-medium text-zinc-600 hover:text-zinc-900 transition-colors">
                        {faviconPreview ? 'Alterar' : 'Carregar'}
                      </span>
                      <input
                        type="file"
                        accept="image/png,image/x-icon,image/svg+xml,image/jpeg"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, 'faviconUrl', setFaviconPreview)}
                      />
                    </label>
                  </div>

                  {/* Preview do browser tab */}
                  <div className="flex-1">
                    <p className="text-[11px] text-zinc-400 mb-2 font-medium">Pré-visualização no browser</p>
                    <div className="bg-zinc-100 rounded-t-lg px-3 py-2 flex items-center gap-2 w-fit max-w-[220px]">
                      {faviconPreview ? (
                        <img src={faviconPreview} alt="favicon" className="w-4 h-4 object-contain flex-shrink-0" />
                      ) : (
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-global-line text-zinc-400 text-xs" />
                        </div>
                      )}
                      <span className="text-[12px] text-zinc-600 truncate font-medium">
                        {theme.nomeCamara || 'Câmara Municipal'}
                      </span>
                      <div className="w-3 h-3 flex items-center justify-center flex-shrink-0 ml-auto">
                        <i className="ri-close-line text-zinc-400 text-xs" />
                      </div>
                    </div>
                    <div className="bg-white border border-zinc-200 rounded-b-lg rounded-tr-lg h-8 w-full max-w-[280px]" />
                    <p className="text-[11px] text-zinc-400 mt-2">PNG, ICO ou SVG · Recomendado 32×32px ou 64×64px</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-zinc-100 rounded-lg flex-shrink-0">
            <i className="ri-eye-line text-zinc-600 text-sm" />
          </div>
          <h2 className="text-[14px] font-semibold text-zinc-800">Pré-visualização</h2>
        </div>
        <div className="p-6">
          <div className="rounded-xl overflow-hidden border border-zinc-200 flex h-40">
            {/* Mini sidebar */}
            <div
              className="w-28 flex flex-col p-3 gap-2 flex-shrink-0"
              style={{
                backgroundColor: theme.sidebarBg,
                backgroundImage: theme.heroImageUrl ? `url(${theme.heroImageUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {theme.logoUrl ? (
                <img src={theme.logoUrl} alt="Logo" className="h-5 object-contain mb-1" />
              ) : (
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-4 h-4 rounded flex-shrink-0" style={{ backgroundColor: theme.sidebarAccent, opacity: 0.8 }} />
                  <div className="h-2 rounded flex-1" style={{ backgroundColor: theme.sidebarAccent, opacity: 0.3 }} />
                </div>
              )}
              {[0.7, 0.4, 0.4, 0.4].map((op, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: theme.sidebarAccent, opacity: op }} />
                  <div className="h-1.5 rounded flex-1" style={{ backgroundColor: theme.sidebarAccent, opacity: op * 0.5 }} />
                </div>
              ))}
            </div>
            {/* Mini content */}
            <div className="flex-1 p-4 flex flex-col gap-2 relative overflow-hidden" style={{ backgroundColor: theme.pageBg }}>
              {theme.pageBgImage && (
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: `url(${theme.pageBgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: (theme.pageBgImageOpacity ?? 15) / 100,
                  }}
                />
              )}
              <div className="h-3 rounded w-24" style={{ backgroundColor: theme.primaryColor, opacity: 0.15 }} />
              <div className="h-5 rounded w-40" style={{ backgroundColor: theme.primaryColor, opacity: 0.8 }} />
              <div className="flex gap-2 mt-1">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex-1 h-10 rounded-lg bg-white border border-zinc-100 p-2 flex flex-col gap-1">
                    <div className="h-1.5 rounded w-8 bg-zinc-200" />
                    <div className="h-2.5 rounded w-6" style={{ backgroundColor: theme.primaryColor, opacity: 0.7 }} />
                  </div>
                ))}
              </div>
              <div className="flex-1 rounded-lg bg-white border border-zinc-100" />
            </div>
          </div>
        </div>
      </div>

      {/* Save */}
      {saved && (
        <div className="flex items-center gap-2.5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-checkbox-circle-fill text-emerald-500 text-sm" />
          </div>
          <p className="text-[13px] text-emerald-700">Aparência guardada com sucesso! As alterações são aplicadas imediatamente.</p>
        </div>
      )}

      <div className="flex justify-end pb-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-2.5 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
        >
          {saving
            ? <><i className="ri-loader-4-line animate-spin" />A guardar...</>
            : <><i className="ri-save-line" />Guardar Aparência</>
          }
        </button>
      </div>
    </div>
  );
}
